import { useCallback, useEffect, useState } from 'react';
import { PlatformApiError, type InvitationRun, type Viewer } from '@sfc/sdk';
import { platform } from '../api/client';
import { Icon } from '../ui/Icon';
import { StatStrip } from '../ui/StatStrip';

function describe(error: unknown): string {
  if (error instanceof PlatformApiError) return error.message;
  return 'Something went wrong. Nobody was written to twice.';
}

/**
 * The one button in this dashboard that writes to people.
 *
 * Thirty-nine people answered the website's signup forms before the matching
 * app existed, and their answers are now accounts and ideas. An account
 * somebody does not know about is not a courtesy -- it is their idea in
 * strangers' decks without their knowledge -- so this is the thing that makes
 * it honest, and it is the last step before the product is open.
 *
 * It asks twice, and the second question spells out what is about to happen
 * in the words somebody will have to defend afterwards. There is no undo for
 * a sent email and the screen does not pretend otherwise.
 */
export function InvitationsPanel({ viewer }: { viewer: Viewer }): React.JSX.Element {
  const organizationId = viewer.organization.id;
  const [waiting, setWaiting] = useState<number>();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<InvitationRun>();
  const [problem, setProblem] = useState<string>();

  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const queue = await platform.staff.readInvitationQueue(organizationId, signal);
        if (!signal?.aborted) setWaiting(queue.waiting);
      } catch (error) {
        if (!signal?.aborted) setProblem(describe(error));
      }
    },
    [organizationId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  async function send(): Promise<void> {
    setBusy(true);
    setProblem(undefined);
    setConfirming(false);
    try {
      const run = await platform.staff.sendInvitations(organizationId);
      setResult(run);
      setWaiting(run.remaining);
    } catch (error) {
      setProblem(describe(error));
    } finally {
      setBusy(false);
    }
  }

  const none = waiting === 0;

  return (
    <section className="card" aria-labelledby="section-heading">
      <div className="invite">
        <p className="detail__text">
          Everybody who signed up on the website before the matching app opened has an account
          here, with what they told us already in it. Most of them have an idea in other people&rsquo;s
          decks. None of them has been told.
        </p>

        <StatStrip
          testId="invitation-stats"
          stats={[
            {
              label: 'Never written to',
              value: waiting ?? 0,
              ...(waiting && waiting > 0 ? { tone: 'down' as const } : {}),
            },
          ]}
        />

        {problem ? (
          <p className="notice notice--problem" role="alert">
            {problem}
          </p>
        ) : null}

        {result ? (
          <p className="notice notice--once" role="status" data-testid="invitation-result">
            {result.sent === 0 && result.failed === 0
              ? 'Nobody was waiting. Nothing was sent.'
              : `${result.sent} written to.`}
            {result.failed > 0
              ? ` ${result.failed} could not be sent and are still waiting — press again.`
              : ''}
            {result.remaining > 0
              ? ` ${result.remaining} still to go; this sends twenty-five at a time.`
              : ''}
          </p>
        ) : null}

        {none ? (
          <p className="detail__text" data-testid="invitation-done">
            Everyone has been told.
          </p>
        ) : confirming ? (
          <div className="invite__confirm" data-testid="invitation-confirm">
            <p className="detail__text">
              This sends an email to <strong>{Math.min(waiting ?? 0, 25)}</strong>{' '}
              {waiting === 1 ? 'person' : 'people'}. Each one gets their own sign-in link, and is
              told how to withdraw their idea before being told anything else.
            </p>
            <p className="detail__text">
              There is no undo. Nobody is written to twice, so a mistake here is a mistake sent once.
            </p>
            <div className="detail__actions">
              <button
                className="button button--danger"
                disabled={busy}
                data-testid="invitation-send"
                onClick={() => void send()}
              >
                Send it
              </button>
              <button className="button" disabled={busy} onClick={() => setConfirming(false)}>
                Not yet
              </button>
            </div>
          </div>
        ) : (
          <div className="detail__actions">
            <button
              className="button button--primary"
              disabled={busy || waiting === undefined}
              data-testid="invitation-start"
              onClick={() => setConfirming(true)}
            >
              <Icon name="submissions" size={16} />
              Tell them
            </button>
            <button className="button" disabled={busy} onClick={() => void load()}>
              <Icon name="refresh" size={16} />
              Refresh
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
